// SAM chat page — a thin container that wires the feature hooks together and picks a layout.
// State + logic live in ./hooks; presentation lives in ./components and @/components/chat.
//
//   useVoiceMode / useAgentSocket  → mic capture, playback, inbound socket events
//   useChatWorkspace               → SAM actions → workspace cards
//   useRecentViews                 → the workspace "⋯" recent-views menu (deduped, persisted)
//   useChatMessaging               → sending messages / decisions / record edits
//   useConversations               → multi-thread session load / new / select / delete
//
// ChatPage owns only the state atoms that several hooks share (assistantId, conversationId,
// messages, …), derives a few display flags, and renders one of four layouts.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { usePCMAudioStream } from '@/hooks/usePCMAudioStream';
import { useListAvailableAgentsQuery } from '@/redux/internal-services/agentsApi';
import {
  useGetClientConfigQuery,
  useGetChatConfigQuery,
  useRecordNoteCapturedMessageMutation,
} from '@/redux/internal-services/agentApi';
import { socketService } from '@/services/SocketService';
import { useAppSelector, useAppDispatch, useRegisterUIContext } from '@/hooks';
import { useTopBarSlot } from '@/components/Layout/TopBarSlot';
import { setActiveConversationId } from '@/redux/slices/uiContextSlice';
import { setPendingVoiceHandoff } from '@/redux/slices/sessionStateSlice';
import { openNotificationsPanel as openNotificationsPanelAction, closeNotificationsPanel } from '@/redux/slices/notificationsPanelSlice';
import type { UIView, UIMode, WorkspaceView } from '@/redux/slices/uiContextSlice';
import { useGetInboxQuery } from '@/redux/internal-services/notificationsApi';
import type { SamModule } from '@/components/SAM/samActionTypes';

// shared chat UI (used across pages)
import { type TextMessage } from '@/components/chat/MessageBubble';
import { UXView, ReplyStrip, type UserViewReport } from '@/components/chat/UXView';
import { contextRefFor } from '@/components/chat/recentViews';
import { DraftPanel } from '@/components/chat/DraftPanel';
import { NotePanel } from '@/components/chat/NotePanel';
import { WorkflowPanel } from '@/components/Workflow/WorkflowPanel';
import { ConfirmCard } from '@/components/chat/ConfirmCard';
import type { Rec } from '@/components/chat/ChatPills';
import type { Note } from '@/redux/internal-services/notesApi';
import { HandsFreeView } from '@/components/chat/HandsFreeView';
import { SessionsDrawer } from '@/components/chat/SessionsDrawer';
import { CaptionBox } from '@/components/chat/CaptionBox';

// cross-page feature hooks
import { useAgentSocket } from '@/hooks/useAgentSocket';
import { useVoiceMode } from '@/hooks/useVoiceMode';
import { useConversations } from '@/hooks/useConversations';
import { useAudioDevices } from '@/hooks/useAudioDevices';

// page-local hooks
import { useIsDesktop } from './hooks/useIsDesktop';
import { useUserContext } from './hooks/useUserContext';
import { useSocketStatus } from './hooks/useSocketStatus';
import { useChatWorkspace } from './hooks/useChatWorkspace';
import { useRecentViews } from './hooks/useRecentViews';
import { useChatMessaging } from './hooks/useChatMessaging';
import { useChatAttachments, type ChatAttachments } from './hooks/useChatAttachments';

// page-local components
import { ChatHeader } from './components/ChatHeader';
import { OfflineBanner } from './components/OfflineBanner';
import { ChatThread } from './components/ChatThread';
import { MessageComposer } from './components/MessageComposer';
import { VoicePills } from './components/VoicePills';
import { InteractiveTextInput } from './components/InteractiveTextInput';

import type { ChatMessage, MobileMode } from './types';

const ChatPage: React.FC = () => {
  const isDesktop = useIsDesktop();
  const dispatch = useAppDispatch();
  // The chat/cards view toggle is portaled into the layout TopBar (next to the profile menu).
  const topBarSlot = useTopBarSlot();
  // Platform admins read users/roles cross-tenant (different REST endpoints); tenant admins
  // hit the tenant-scoped defaults baked into the workspace API's MODULE_PATH.
  const sessionRoles = useAppSelector((s) => s.sessionState.roles);
  const isPlatform = sessionRoles.includes('platform_admin') || sessionRoles.includes('platform_support');
  // One-shot signal set by SetupWizardPage right before navigating here, only when the user
  // finished onboarding with an onboarding voice ("Talk to SAM") session live — see the
  // auto-start-interactive effect below. Replaces the old samWelcomeShown ("is this a brand-new
  // user") based auto-start: that fired for every first-time user regardless of which mode they
  // actually used, and never fired for a returning user resuming from voice.
  const pendingVoiceHandoff = useAppSelector((s) => s.sessionState.pendingVoiceHandoff);

  // ── shared state atoms (wired into several hooks, so they live at the top) ──────
  const [assistantId, setAssistantId] = useState('');
  const [conversationId, setConversationId] = useState('');

  // Desktop split: workspace panel width as a percentage. Default 45% gives the workspace
  // more room per PM feedback. Persisted in localStorage so the user's preference survives reload.
  const [workspacePct, setWorkspacePct] = useState<number>(() => {
    const saved = localStorage.getItem('chatWorkspacePct');
    return saved ? Math.min(70, Math.max(25, Number(saved))) : 45;
  });
  const splitDragging = useRef(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  // Kept in sync so the window-level mouseup listener below can persist the LATEST width even
  // though its effect only runs once on mount (empty deps — attaching/detaching on every
  // workspacePct change would be wasteful).
  const workspacePctRef = useRef(workspacePct);
  useEffect(() => { workspacePctRef.current = workspacePct; }, [workspacePct]);
  // A drag started with onMouseDown (below) only ends via onMouseUp/onMouseLeave scoped to the
  // split container — if the user releases the button outside the viewport (fast drag past the
  // window edge, or over browser/OS chrome), neither fires, so splitDragging.current is stuck
  // `true` and the NEXT mousemove over the container silently resumes resizing with no button
  // held. A window-level mouseup always fires on release regardless of where the cursor ends up.
  useEffect(() => {
    const onWindowMouseUp = () => {
      if (!splitDragging.current) return;
      splitDragging.current = false;
      localStorage.setItem('chatWorkspacePct', String(workspacePctRef.current));
    };
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => window.removeEventListener('mouseup', onWindowMouseUp);
  }, []);
  // Publish the active conversation id to the global uiContext slice so the always-mounted
  // UIContextBridge (outside this component tree) can key its agent_realtime.state_sync pushes
  // to it; cleared on unmount so leaving chat doesn't leave a stale id behind.
  useEffect(() => {
    dispatch(setActiveConversationId(conversationId || null));
    return () => { dispatch(setActiveConversationId(null)); };
  }, [dispatch, conversationId]);
  // ── Inline approval / notifications panel — opened via URL params ──
  const [searchParams, setSearchParams] = useSearchParams();
  const [openApprovalId, setOpenApprovalId] = useState<string | null>(null);
  // Lifted to redux so NotificationBell (in TopBar, outside this component) can toggle it closed
  // on a second click — see notificationsPanelSlice.ts.
  const openNotificationsPanel = useAppSelector((s) => s.notificationsPanel.isOpen);
  // directEngagementId lifted from UXView so ChatPage can keep workspace visible on mobile.
  const [directEngagementId, setDirectEngagementId] = useState<string | null>(null);
  const [directTaskId, setDirectTaskId] = useState<string | null>(null);
  const [directMeetingId, setDirectMeetingId] = useState<string | null>(null);
  useEffect(() => {
    const approvalId = searchParams.get('approval');
    const notificationsFlag = searchParams.get('notifications');
    const engagementId = searchParams.get('engagement');
    const taskId = searchParams.get('task');
    const meetingId = searchParams.get('meeting');
    if (approvalId || notificationsFlag === 'open' || engagementId || taskId || meetingId) {
      if (approvalId) setOpenApprovalId(approvalId);
      if (notificationsFlag === 'open') dispatch(openNotificationsPanelAction());
      if (engagementId) setDirectEngagementId(engagementId);
      if (taskId) setDirectTaskId(taskId);
      if (meetingId) setDirectMeetingId(meetingId);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Emit agent UI context when the notifications panel opens from the bell icon
  useEffect(() => {
    if (openNotificationsPanel) {
      openCardByUser('notifications', null);
      if (!isDesktop) setChatOn(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNotificationsPanel, isDesktop]);
  // On mobile, switch to workspace view when an approval or direct engagement opens via URL param
  useEffect(() => {
    if ((openApprovalId || directEngagementId) && !isDesktop) setChatOn(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openApprovalId, directEngagementId, isDesktop]);
  // Open task card with the specific task expanded when navigated from a notification
  useEffect(() => {
    if (!directTaskId) return;
    setOpenCards(prev => ({ ...prev, tasks: directTaskId }));
    openCardByUser('tasks', directTaskId);
    if (!isDesktop) setChatOn(false);
    setDirectTaskId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directTaskId, isDesktop]);
  // Open meeting card with the specific meeting shown when navigated from a notification
  useEffect(() => {
    if (!directMeetingId) return;
    setOpenCards(prev => ({ ...prev, meetings: directMeetingId }));
    openCardByUser('meetings', directMeetingId);
    if (!isDesktop) setChatOn(false);
    setDirectMeetingId(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directMeetingId, isDesktop]);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [captionsOn, setCaptionsOn] = useState(true);
  const [liveCaption, setLiveCaption] = useState('');
  const [mobileMode, setMobileMode] = useState<MobileMode>('chat');
  const [chatOn, setChatOn] = useState(true);
  const [isSwitchingView, setIsSwitchingView] = useState(false);
  const switchingRef = useRef(false);
  const switchView = useCallback((next: React.SetStateAction<boolean>) => {
    if (switchingRef.current) return;
    switchingRef.current = true;
    setIsSwitchingView(true);
    setTimeout(() => {
      setChatOn(next);
      setIsSwitchingView(false);
      switchingRef.current = false;
    }, 600);
  }, []);
  const [hfIndex, setHfIndex] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const realtimeAsstIdRef = useRef('');
  // Holds the attachments controller so useVoiceMode (declared earlier) can read staged files for a
  // PTT turn without a circular dependency on the hook below.
  const attachmentsRef = useRef<ChatAttachments | null>(null);

  const audioOut = usePCMAudioStream();
  useUserContext(); // captures timezone + geolocation into the global store (sent with each turn)
  const socketConnected = useSocketStatus();

  // Audio device pickers (⋮ menu). '' = system default. Output switching is Chromium-desktop only.
  const { inputs: audioInputs, outputs: audioOutputs, outputSupported, refresh: refreshDevices } = useAudioDevices();
  const [inputDeviceId, setInputDeviceId] = useState('');
  const [outputDeviceId, setOutputDeviceId] = useState('');

  // ── agent + config ─────────────────────────────────────────────────────────
  const { data: agents } = useListAvailableAgentsQuery();
  const { data: clientConfig } = useGetClientConfigQuery();
  const { data: chatConfig } = useGetChatConfigQuery(
    { agentId: assistantId }, { skip: !assistantId },
  );
  const [recordNoteCapturedMessage] = useRecordNoteCapturedMessageMutation();
  const multiThread = !!chatConfig?.multi_thread_allowed;
  // Workspace display (title header + caption in the "⋯") is always on; the inline chat card is opt-in.
  const inlineActionCards = !!chatConfig?.action_card_inline;
  // Debug: per-turn "View agent steps" inspector under each assistant reply.
  const debugMode = !!chatConfig?.debug_mode;
  // Mobile workspace: show a "Tap to open chat" link on the reply summary above the cards.
  const tapToOpenChat = !!chatConfig?.tap_to_open_chat;
  // Interactive (voice) session: also show a text input so the user can type to the live agent.
  const interactiveTextInputEnabled = !!chatConfig?.interactive_text_input_enabled;
  // Voice note dictation: spoken stop word (hands-free). undefined when the setting is off.
  const noteStopWord = chatConfig?.note_stop_word_enabled ? (chatConfig?.note_stop_word || 'stop recording') : undefined;
  // Feedback: show "Was this helpful?" prompt below each completed assistant reply.
  const feedbackEnabled = !!chatConfig?.feedback;

  // ── voice + inbound socket ───────────────────────────────────────────────────
  const {
    mode, recording, playbackRef,
    startPtt, sendPtt, cancelPtt, pausePtt, resumePtt, lockPtt,
    paused, pttLocked, recordMs, pttLevels, pttPlaying, playPtt, stopPttPlayback,
    toggleInteractive, stopInteractive, sendRealtimeText,
    micMuted, toggleMicMute, speakerMuted, toggleSpeakerMute, restartCapture,
  } = useVoiceMode({
    conversationId,
    assistantId,
    audioOut,
    setConversationId,
    setMessages: setMessages as React.Dispatch<React.SetStateAction<{ kind: string; id: string; streaming?: boolean }[]>>,
    setLiveCaption,
    sttProvider: clientConfig?.ai?.stt_provider,
    getPendingFiles: () => attachmentsRef.current?.attachments ?? [],
    onFilesConsumed: () => attachmentsRef.current?.clear(),
    inputDeviceId,
  });

  // Route SAM's playback to the chosen output device (no-op where setSinkId is unsupported).
  useEffect(() => { audioOut.setSinkId(outputDeviceId); }, [outputDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Re-acquire the mic when the user picks a different input device during a live session.
  useEffect(() => { if (recording) restartCapture(); }, [inputDeviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useAgentSocket({
    audioOut,
    playbackRef,
    realtimeAsstIdRef,
    setMessages,
    setConversationId,
    setLiveCaption,
    onFirstResponse: () => setIsWaiting(false),
  });

  // SAM EXECUTE_NOTE_FOLLOWUPS — the realtime agent can trigger "Create all selected" on the open
  // note review card. NoteReviewCard registers its handleDoneAll here via onExecuteFollowupsRef.
  const executeFollowupsRef = useRef<(() => void) | null>(null);
  const onExecuteNoteFollowups = useCallback(() => {
    executeFollowupsRef.current?.();
  }, []);

  // SAM SET_NOTE_FOLLOWUP_INCLUDED — check/uncheck one, several, or all follow-up suggestions on
  // the open note review card. NoteReviewCard registers its setIncluded updater here.
  const toggleFollowupsRef = useRef<((opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => void) | null>(null);
  const onSetNoteFollowupIncluded = useCallback((opts: { suggestionId?: string; suggestionIds?: string[]; included: boolean }) => {
    toggleFollowupsRef.current?.(opts);
  }, []);

  // SAM EXPAND_NOTE_FOLLOWUP — expand a follow-up's review/edit panel (no create), optionally
  // prefilling fields. NoteReviewCard registers the expand function here.
  const expandFollowupRef = useRef<((opts: { suggestionId: string; fields?: Record<string, unknown> }) => void) | null>(null);
  const onExpandNoteFollowup = useCallback((opts: { suggestionId: string; fields?: Record<string, unknown> }) => {
    expandFollowupRef.current?.(opts);
  }, []);

  // ── workspace cards engine + conversation sessions ─────────────────────────────
  // The "⋯" recent-views menu, backed by `workspace_recent_views`. Declared before the cards
  // engine because that hook records every surface it opens through `recentViews.record`.
  const recentViews = useRecentViews(conversationId, chatConfig?.recent_views_limit);

  // A workspace view the user reached without an agent turn — a manual filter on a list card, or
  // an engagement started from a property card's "+". Recorded with a non-agent source, which is
  // what persists it (the agent path is written server-side by publish_action instead).
  // Depends on `record` (a stable useCallback), not the `recentViews` object, which is rebuilt every
  // render — otherwise this handler's identity churns down through UXView's context provider.
  const recordView = recentViews.record;
  const resetRecentViews = recentViews.reset;
  const recordUserView = useCallback((view: UserViewReport) => {
    recordView({
      cardType: view.module,
      title: view.title,
      subtitle: view.subtitle,
      viewParams: view.params,
      contextRef: contextRefFor(view.module, view.recordId),
      source: view.recordId ? 'user_create' : 'user_filter',
    });
  }, [recordView]);

  const {
openCards,
setOpenCards,
hidePropertyBack,
clearSamOpenedProperty,
taskTab,
setTaskTab,
meetingTab,
replayView,
openActionView,
workspaceView,
selectUser,
latestCards,
latestDraft,
latestNote,
latestWorkflow,
latestConfirm,
latestSurface,
emitDraftContext,
emitNoteContext,
emitTaskContext,
emitMeetingContext,
emitWorkflowContext,
emitWorkflowSettingsContext,
openCardByUser,
closeNoteCard,
upsertNoteCard,
refreshNotesList,
openWorkflowSettings,
 } = useChatWorkspace({
    messages, setMessages, conversationId, mode, isDesktop, isPlatform, inlineActionCards,
    threadRef, setChatOn: switchView, setHfIndex,
    // SAM start_note_taker opens the note-capture card IN THE WORKSPACE (useChatWorkspace upserts it
    // + brings the surface into view). No navigation, and the interactive session stays alive so the
    // agent can narrate the note on stop — NotePanel just mutes the mic during dictation.
    onExecuteNoteFollowups,
    onSetNoteFollowupIncluded,
    onExpandNoteFollowup,
    openNotificationsPanel,
    recordView,
  });

  const { allSessions, loadSession, startNewChat, selectSession, removeSession, loadOlder, hasMore, loadingOlder } = useConversations({
    assistantId,
    conversationId,
    multiThread,
    inlineCards: inlineActionCards,
    setConversationId,
    setMessages,
  });

  // When SAM opens a new workspace card while the notifications panel is open (e.g. search_engagement's show_panel),
  // dismiss the notifications panel so the new card is visible. Uses a prev-ref so we only close on
  // a SAM-driven surface change, not on initial conversation load (null → first surface).
  const prevSurfaceRef = useRef<typeof latestSurface | null>(null);
  useEffect(() => {
    const prev = prevSurfaceRef.current;
    prevSurfaceRef.current = latestSurface;
    if (prev && latestSurface && latestSurface !== prev && openNotificationsPanel) {
      if (latestSurface.kind === 'cards' && (latestSurface as { module?: string }).module !== 'notifications') {
        dispatch(closeNotificationsPanel());
      }
    }
  }, [latestSurface, openNotificationsPanel]);

  // ── messaging ──────────────────────────────────────────────────────────────
  // Keep a ref so ensureConversationId can use the default_session_id synchronously even
  // before chatConfig has set conversationId via the loadSession effect below.
  const defaultSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (chatConfig?.default_session_id) defaultSessionIdRef.current = chatConfig.default_session_id;
  }, [chatConfig?.default_session_id]);

  const ensureConversationId = useCallback(() => {
    if (conversationId) return conversationId;
    // Prefer the known default session over a random UUID — a random UUID creates an orphan
    // session in the DB that the chat-config endpoint never returns, silently losing messages.
    const id = defaultSessionIdRef.current ?? crypto.randomUUID();
    setConversationId(id);
    return id;
  }, [conversationId]);

  const { send, retryMessage, sendDecision, handleCardSave, handleFacetSelect } = useChatMessaging({
    assistantId, conversationId, ensureConversationId, setMessages, setInput, threadRef,
  });

  // File attachments (+ button). Interactive mode dispatches immediately (image → vision, file →
  // KB); text/PTT stage chips that ride with the next turn. Exposed via ref for the PTT path.
  const attach = useChatAttachments({
    cfg: chatConfig?.file_upload,
    isInteractive: mode === 'interactive',
    conversationId,
    onLocalEcho: (files) => setMessages(prev => [...prev, {
      kind: 'text', id: `u-${Date.now()}`, role: 'user', text: '', files, createdAt: Date.now(),
    } as ChatMessage]),
  });
  attachmentsRef.current = attach;
  const fileAccept = (chatConfig?.file_upload?.allowed_types || []).map((t) => `.${t}`).join(',') || undefined;

  // Send a text turn with any staged attachments, then clear them.
  const submit = useCallback(() => {
    if (!input.trim() && !attach.attachments.length) return;
    if (!send(input, attach.attachments)) return; // no-op turn (not ready / offline) — don't fake a reply in flight
    attach.clear();
    setIsWaiting(true);
  }, [send, input, attach]);

  // ── effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (agents?.length && !assistantId) setAssistantId(agents[0].agent_id);
  }, [agents, assistantId]);

  // Auto-start interactive mode when the user arrives here having just been in an onboarding
  // voice ("Talk to SAM") session — see pendingVoiceHandoff's doc comment. NOT based on whether
  // this is a brand-new user: a new user who onboarded via text stays in text mode, and this
  // fires just as well for a returning user finishing onboarding with voice. Fires once as soon
  // as the agent is ready; the ref guard prevents re-entry, and clearing the redux flag
  // immediately means a later remount within the same SPA session (no reload) can't refire it.
  const voiceHandoffConsumedRef = useRef(false);
  // Claimed on the first render that sees the flag, so it survives here until we can act on it.
  const voiceHandoffClaimedRef = useRef(false);
  useEffect(() => {
    if (voiceHandoffConsumedRef.current) return;
    if (!pendingVoiceHandoff && !voiceHandoffClaimedRef.current) return;
    if (pendingVoiceHandoff) {
      // Clear redux the moment we see it, BEFORE the assistantId/mode gate below. The agent list
      // usually lands a beat after mount, so the gate legitimately bails on the first pass — but
      // if it never resolves (failed agents query) an uncleared flag would sit in redux and
      // auto-start voice on some later, unrelated visit to Chat in the same SPA session.
      voiceHandoffClaimedRef.current = true;
      dispatch(setPendingVoiceHandoff(false));
    }
    if (!assistantId || mode !== 'text') return;
    voiceHandoffConsumedRef.current = true;
    voiceHandoffClaimedRef.current = false;
    toggleInteractive();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantId, pendingVoiceHandoff, mode]);

  // Pin to default session when multi-threading is OFF.
  useEffect(() => {
    if (!chatConfig || chatConfig.multi_thread_allowed) return;
    const def = chatConfig.default_session_id;
    if (def && def !== conversationId) loadSession(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatConfig]);

  // Halt audio playback on unmount.
  const audioOutRef = useRef(audioOut); audioOutRef.current = audioOut;
  useEffect(() => () => {
    playbackRef.current = false;
    audioOutRef.current.stop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers ─────────────────────────────────────────────────────────────────
  const selectAgent = useCallback((id: string) => {
    if (id === assistantId) return;
    setAssistantId(id);
    setConversationId('');
    setMessages([]);
    resetRecentViews();
  }, [assistantId, resetRecentViews]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const enterHandsFree = useCallback(async () => {
    setMobileMode('handsfree');
    if (mode !== 'interactive') await toggleInteractive();
  }, [mode, toggleInteractive]);

  const exitHandsFree = useCallback(() => {
    if (mode === 'interactive') stopInteractive();
    setMobileMode('chat');
  }, [mode, stopInteractive]);

  // Open the chat thread (mobile workspace → thread) and remember where to scroll once it mounts:
  // to a specific reply (its message id, for "Tap to open chat") or the bottom (the latest reply).
  const pendingScrollRef = useRef<string | null>(null);
  const openChat = useCallback((messageId?: string) => {
    pendingScrollRef.current = messageId || 'bottom';
    switchView(true);
  }, [switchView]);

  useEffect(() => {
    if (!chatOn) return;
    const target = pendingScrollRef.current;
    pendingScrollRef.current = null;
    // The thread mounts when chatOn flips on; wait a tick for layout before scrolling.
    const t = setTimeout(() => {
      const el = threadRef.current;
      if (!el) return;
      const node = target && target !== 'bottom' ? el.querySelector<HTMLElement>(`[data-mid="${target}"]`) : null;
      if (node) node.scrollIntoView({ block: 'start' });
      else el.scrollTo({ top: el.scrollHeight });
    }, 60);
    return () => clearTimeout(t);
  }, [chatOn]);

  // Push a live "what the user is seeing" patch to the realtime voice session (no-op otherwise).
  // The backend (receive_context_update) turns it into a [SCREEN UPDATE …] message for the model.
  // De-duped like useUIContextBridge's state_sync push: without this, an unrelated re-render (e.g.
  // React 18 StrictMode's double-invoke of mount-time effects, or another dep settling to the same
  // value) re-fires the effect below with an identical patch, sending the same context_patch twice
  // over the socket — and since the backend turns every context note into a spoken model turn, one
  // real screen switch was producing multiple duplicate voice responses.
  const lastScreenPatchRef = useRef<string>('');
  const notifyRealtimeScreen = useCallback((patch: Record<string, unknown>) => {
    if (mode !== 'interactive' || !conversationId) return;
    const serialized = JSON.stringify(patch);
    if (serialized === lastScreenPatchRef.current) return;
    lastScreenPatchRef.current = serialized;
    socketService.emit('message', { type: 'agent_realtime.session.update', session_id: conversationId, context_patch: patch });
  }, [mode, conversationId]);

  // Draft-card lifecycle in the workspace surface. Discard removes the card and clears the draft
  // context; send keeps the card (it flips to the delivery timeline in place) but clears the draft
  // context since it's no longer editable.
  const discardDraftCard = useCallback(() => {
    setMessages(prev => prev.filter(m => m.kind !== 'draftcard'));
    emitDraftContext(null);
  }, [emitDraftContext]);
  const onDraftSent = useCallback(() => { emitDraftContext(null); }, [emitDraftContext]);

  // Note-capture voice handoff: fully pause the interactive voice session (not just mute it) while
  // the user dictates a note — stopInteractive ends the realtime connection outright, so there's no
  // chance of live mic audio / VAD reaching a still-open session mid-dictation. If voice mode was
  // actually active when capture started, resume it on stop (toggleInteractive reconnects on the
  // SAME conversation) and re-push the note context once the fresh session exists, so it's the new
  // session that narrates — pushing it any earlier would target a session that no longer exists.
  // No-op outside an interactive voice session (started via button while in text/PTT mode stays put).
  const wasInteractiveRef = useRef(false);
  const onNoteCaptureStart = useCallback(() => {
    wasInteractiveRef.current = mode === 'interactive';
    if (wasInteractiveRef.current) stopInteractive();
  }, [mode, stopInteractive]);
  const onNoteCaptureStop = useCallback((n?: Note | null) => {
    if (wasInteractiveRef.current) {
      wasInteractiveRef.current = false;
      void (async () => {
        await toggleInteractive({ post_note: true });
        if (n) emitNoteContext(n as unknown as Rec);
      })();
      return;
    }
    // Text/PTT mode: the workspace's own Stop button ends capture client-side (recorder.stop() in
    // NotePanel) — it never calls the stop_note_taker SAM tool, so no new agent turn runs. Without
    // this, the thread's last visible message stays frozen on start_note_taker's reply (whatever
    // SAM said when recording started) even after the note is fully captured, analysed, and its
    // review card is already showing — reading as if recording were still in progress. Post a
    // short synthetic assistant bubble so the thread reflects what actually happened.
    if (n) {
      const count = n.suggestions?.length ?? 0;
      const summary = n.review_summary || n.summary;
      const text = summary
        ? `${summary}${count > 0 ? ` I found ${count} follow-up${count === 1 ? '' : 's'} — review and create them on the right.` : ''}`
        : 'Note captured — review it on the right.';
      setMessages(prev => [...prev, {
        kind: 'text', id: `note-captured-${Date.now()}`, role: 'assistant', text, createdAt: Date.now(),
      } as ChatMessage]);
      // Persist the synthetic bubble server-side so it survives reload and shows up in
      // conversation_runs traces (fire-and-forget — never block the UI on this).
      if (conversationId) {
        void recordNoteCapturedMessage({
          conversationId,
          text,
          noteId: (n as unknown as { note_id?: string }).note_id,
        }).unwrap().catch(() => {});
      }
    }
  }, [toggleInteractive, emitNoteContext, setMessages, conversationId, recordNoteCapturedMessage]);
  const onNoteActive = useCallback((n: Note | null) => emitNoteContext(n ? (n as unknown as Rec) : null), [emitNoteContext]);

  // ── derived view state ───────────────────────────────────────────────────────
  const isHandsFree = mode === 'interactive';
  const isPtt = mode === 'ptt';
  const selectedAgent = agents?.find((a) => a.agent_id === assistantId);
  const voiceCfg = (selectedAgent?.voice as { interaction_modes_allowed?: string[] } | undefined) || {};
  const modesAllowed = voiceCfg.interaction_modes_allowed || ['text', 'push_to_talk', 'interactive'];
  const caption = captionsOn && !(isPtt && !recording) ? liveCaption : '';
  // Drafts live in the workspace surface (not the thread); a draft is the current surface when it is
  // the most recent of the cards/draft messages.
  const draftIsCurrent = latestSurface?.kind === 'draftcard';
  const noteIsCurrent = latestSurface?.kind === 'notecard';
  const workflowIsCurrent = latestSurface?.kind === 'workflowcard';
  // A pending confirmation lives ONLY in the workspace panel (never the chat thread) — on mobile it
  // takes over the surface (auto-switched via setChatOn(false) in useChatWorkspace's onConfirmation);
  // on desktop it opens the same right-hand panel every other surface uses. Resolved confirms drop
  // out of latestSurface immediately, handing control back to whatever surface was showing before.
  const confirmIsCurrent = latestSurface?.kind === 'confirm';
  // True when there is a workspace surface (cards/draft/note/workflow/confirm) to show on desktop.
  const hasWorkspaceSurface = !!(latestCards || latestDraft || latestNote || latestWorkflow || confirmIsCurrent);
  const chatMessages = messages.filter(m => m.kind === 'text' || m.kind === 'action');
  const latestAssistantText =
    [...messages].reverse().find(m => m.kind === 'text' && (m as TextMessage).role === 'assistant')?.kind === 'text'
      ? ([...messages].reverse().find(m => m.kind === 'text' && (m as TextMessage).role === 'assistant') as TextMessage).text
      : '';
const turnMatchedText = latestSurface?.messageId
  ? (messages.find(
      m => m.kind === "text" && m.id === latestSurface.messageId
    ) as TextMessage | undefined)?.text
  : undefined;

const cardResponseText = turnMatchedText ?? latestAssistantText;
  const showHandsFree = mobileMode === 'handsfree' && !isDesktop;
  const showChat = chatOn;
  // Notifications panel (bell-click or approval URL param) or direct engagement counts as a surface.
  const hasNotificationsOrApproval = !!(openNotificationsPanel || openApprovalId);
  const hasExtraWorkspaceSurface = hasNotificationsOrApproval || !!directEngagementId;
  // Mobile workspace surface: only when there is a surface (cards OR draft) to show, OR one of the
  // overlay panels (notifications, approval, direct engagement) is active.
  const showWorkspace = !isDesktop && !showChat && (!!latestSurface || hasExtraWorkspaceSurface);
  const chatVisible = isDesktop || showChat || (!latestSurface && !hasExtraWorkspaceSurface);
  // Mobile interactive, surface on screen (thread hidden): the only place the assistant caption +
  // CC toggle survive, since the reply isn't visible as a bubble.
  const showCardsSurface = isHandsFree && showWorkspace;

  const inVoice = isHandsFree || showHandsFree;
  const showAgentPicker = !inVoice && (agents?.length ?? 0) > 1;
  const showSessionsBtn = !inVoice && multiThread;
  // The chat header only renders when it has content (sessions / agent picker / hands-free
  // badge). The mobile chat↔cards toggle now lives in the layout TopBar, so it no longer forces
  // an otherwise-empty header on mobile.
  const headerVisible = showHandsFree || showAgentPicker || showSessionsBtn;
  // Mobile chat↔cards toggle (portaled into the TopBar): only when there is a surface to switch to,
  // and never in the dedicated hands-free view.
  const showViewToggle = !isDesktop && !showHandsFree && (!!latestSurface || hasExtraWorkspaceSurface);

  // When the notifications panel is open, fetch inbox items (shares RTK cache with WorkspaceNotificationsPanel).
  const { data: inboxData } = useGetInboxQuery(
    { archived: false, limit: 20, skip: 0 },
    { skip: !openNotificationsPanel },
  );
  // Override workspaceView with real notification items when the panel is open so SAM can
  // read and reference the visible notifications in conversation.
  const effectiveWorkspaceView = useMemo<WorkspaceView | null>(() => {
    if (openNotificationsPanel) {
      return {
        kind: 'workspace' as const,
        module: 'notifications' as SamModule,
        filter: {},
        title: 'Notifications',
        items: (inboxData?.items ?? []).map((n, i) => ({
          index: i + 1,
          id: n.id,
          title: n.title,
          status: n.classification as string,
        })),
        openCardId: null,
      };
    }
    return workspaceView;
  }, [openNotificationsPanel, inboxData, workspaceView]);

  // Publish the surfaces the user is ACTUALLY seeing into the global UI-context store. Hands-free
  // shows the card view (or bubbles when no cards); desktop shows both; mobile (text or interactive)
  // shows exactly one per the chat/cards toggle.
  const visibleSurfaces = useMemo<UIView[]>(() => {
    if (showHandsFree) return effectiveWorkspaceView ? [effectiveWorkspaceView] : [{ kind: 'chat' }];
    if (isDesktop) return effectiveWorkspaceView ? [{ kind: 'chat' }, effectiveWorkspaceView] : [{ kind: 'chat' }];
    return showChat ? [{ kind: 'chat' }] : (effectiveWorkspaceView ? [effectiveWorkspaceView] : [{ kind: 'chat' }]);
  }, [showHandsFree, isDesktop, showChat, effectiveWorkspaceView]);
  const primaryMode: UIMode = visibleSurfaces[0]?.kind === 'workspace' ? 'workspace' : 'chat';
  useRegisterUIContext({ mode: primaryMode, surfaces: visibleSurfaces }, [primaryMode, visibleSurfaces]);

   // Hands-free: the single big card on screen IS the selected user. Push it as the rename target
  // whenever the user navigates (or the list changes) so "rename this" resolves in voice modes too.
  useEffect(() => {
    if (!showHandsFree || draftIsCurrent || noteIsCurrent || workflowIsCurrent || confirmIsCurrent) return;
    if (!latestCards || latestCards.module !== 'users') return;
    const recs = latestCards.records;
    selectUser(recs.length ? (recs[Math.min(hfIndex, recs.length - 1)] ?? null) : null);
  }, [showHandsFree, draftIsCurrent, noteIsCurrent, workflowIsCurrent, confirmIsCurrent, latestCards, hfIndex, selectUser]);

  // Tell the live voice agent which surface the user is on (mobile interactive chat ↔ workspace).
  useEffect(() => {
    if (!isHandsFree || isDesktop || showHandsFree) return;
    notifyRealtimeScreen({ view: showWorkspace ? 'workspace' : 'chat' });
  }, [isHandsFree, isDesktop, showHandsFree, showWorkspace, notifyRealtimeScreen]);

  // Tell the live voice agent which hands-free card is on screen (swipe / jump / list change). Not
  // for a draft — the draft's live state rides the separate active_draft patch, not a card index.
  useEffect(() => {
    if (!showHandsFree || draftIsCurrent || noteIsCurrent || workflowIsCurrent || confirmIsCurrent) return;
    const items = effectiveWorkspaceView?.items ?? [];
    const total = items.length;
    if (!total) return;
    const pos = Math.min(hfIndex, total - 1);
    notifyRealtimeScreen({ current_card: { index: pos + 1, total, title: items[pos]?.title } });
  }, [showHandsFree, draftIsCurrent, noteIsCurrent, workflowIsCurrent, confirmIsCurrent, effectiveWorkspaceView, hfIndex, notifyRealtimeScreen]);

 // ── shared layout building blocks ──────────────────────────────────────────────
  const thread = (
    <ChatThread threadRef={threadRef} messages={chatMessages} onActionView={openActionView} onRetryMessage={retryMessage} conversationId={conversationId} debugMode={debugMode} feedbackEnabled={feedbackEnabled} isWaiting={isWaiting} hasMore={hasMore} loadingOlder={loadingOlder} onLoadOlder={loadOlder} />
  );
  // The workspace surface (list cards OR the editable draft) rendered as a right/side panel.
  // Task and meeting detail views are handled inside UXView so the workspace header (title + ↺ + ⋯)
  // always stays visible — same pattern as the Properties detail view.
  // `showWaiting` lets a caller that deliberately blanks `messageStrip` (desktop split-view and
  // hands-free mobile — see the two call sites below — pass false so isWaiting doesn't leak
  // through unfiltered) suppress the dots there too, instead of only suppressing the text.
  const surfacePanel = (messageStrip: string, onExpandStrip?: () => void, showWaiting: boolean = true) => (
    confirmIsCurrent && latestConfirm ? (
      // A tool confirmation is pending — rendered ONLY here (never in the chat thread). Deciding
      // hands the panel back to whatever was showing before, since latestSurface drops resolved
      // confirm messages once the tool finishes.
      <div className="flex flex-col h-full min-h-0">
        <ReplyStrip text={messageStrip} onExpand={onExpandStrip} isWaiting={showWaiting && isWaiting} />
        <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0">
          <ConfirmCard msg={latestConfirm} onDecide={(d) => sendDecision(latestConfirm.id, latestConfirm.action, d)} />
        </div>
      </div>
    ) : (openNotificationsPanel || openApprovalId || directEngagementId) ? (
      // Bell / URL-param panels take priority over any SAM-driven surface so the user can always
      // reach notifications, approval detail, or a direct engagement from wherever they are.
      <UXView cards={latestCards} messageStrip={messageStrip} onExpandStrip={onExpandStrip} isWaiting={showWaiting && isWaiting} onSave={handleCardSave} onFacet={handleFacetSelect} openCards={openCards} setOpenCards={setOpenCards} hidePropertyBack={hidePropertyBack} onUserOpenProperty={clearSamOpenedProperty} history={recentViews.entries} onReplay={replayView} historyHasMore={recentViews.hasMore} onHistoryLoadMore={recentViews.loadMore} taskTab={taskTab} setTaskTab={setTaskTab} meetingTab={meetingTab} onActiveTask={emitTaskContext} onActiveMeeting={emitMeetingContext} onNoteSynced={emitNoteContext} onStartNoteTaker={upsertNoteCard} onNotesListChanged={refreshNotesList} openApprovalId={openApprovalId} onCloseApproval={() => setOpenApprovalId(null)} onUserOpenCard={openCardByUser} onEngagementStateChange={emitWorkflowContext} onApprovalSelected={(id) => openCardByUser('notifications', id)} openNotificationsPanel={openNotificationsPanel} onCloseNotificationsPanel={() => { dispatch(closeNotificationsPanel()); setMessages(prev => prev.filter(m => !(m.kind === 'cards' && (m as any).module === 'notifications'))); }} onOpenActionView={openActionView} onUserView={recordUserView} directEngagementId={directEngagementId} onDirectEngagementChange={setDirectEngagementId} />
    ) : noteIsCurrent && latestNote ? (
      <NotePanel
        note={latestNote}
        messageStrip={messageStrip}
        onExpandStrip={onExpandStrip}
        isWaiting={showWaiting && isWaiting}
        history={recentViews.entries}
        onReplay={replayView}
        historyHasMore={recentViews.hasMore}
        onHistoryLoadMore={recentViews.loadMore}
        onClose={closeNoteCard}
        onCaptureStart={onNoteCaptureStart}
        onCaptureStop={onNoteCaptureStop}
        onActiveNote={onNoteActive}
        onOpenTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); openCardByUser('tasks', id); }}
        stopWord={noteStopWord}
        onExecuteFollowupsRef={executeFollowupsRef}
        onToggleFollowupsRef={toggleFollowupsRef}
        onExpandFollowupRef={expandFollowupRef}
        onNotesListChanged={refreshNotesList}
      />
    ) : draftIsCurrent && latestDraft ? (
      <DraftPanel
        draft={latestDraft}
        messageStrip={messageStrip}
        onExpandStrip={onExpandStrip}
        isWaiting={showWaiting && isWaiting}
        history={recentViews.entries}
        onReplay={replayView}
        historyHasMore={recentViews.hasMore}
        onHistoryLoadMore={recentViews.loadMore}
        onDiscard={discardDraftCard}
        onSent={onDraftSent}
        onDraftEdit={emitDraftContext}
      />
    ) : workflowIsCurrent && latestWorkflow ? (
      <WorkflowPanel
        workflow={latestWorkflow}
        messageStrip={messageStrip}
        onExpandStrip={onExpandStrip}
        isWaiting={showWaiting && isWaiting}
        history={recentViews.entries}
        onReplay={replayView}
        historyHasMore={recentViews.hasMore}
        onHistoryLoadMore={recentViews.loadMore}
        onStateChange={emitWorkflowContext}
        onSettingsStateChange={emitWorkflowSettingsContext}
        onCustomize={openWorkflowSettings}
        onOpenEngagement={setDirectEngagementId}
      />
    ) : (
      <UXView cards={latestCards} messageStrip={messageStrip} onExpandStrip={onExpandStrip} isWaiting={showWaiting && isWaiting} onSave={handleCardSave} onFacet={handleFacetSelect} openCards={openCards} setOpenCards={setOpenCards} hidePropertyBack={hidePropertyBack} onUserOpenProperty={clearSamOpenedProperty} history={recentViews.entries} onReplay={replayView} historyHasMore={recentViews.hasMore} onHistoryLoadMore={recentViews.loadMore} taskTab={taskTab} setTaskTab={setTaskTab} meetingTab={meetingTab} onActiveTask={emitTaskContext} onActiveMeeting={emitMeetingContext} onNoteSynced={emitNoteContext} onStartNoteTaker={upsertNoteCard} onNotesListChanged={refreshNotesList} openApprovalId={openApprovalId} onCloseApproval={() => setOpenApprovalId(null)} onUserOpenCard={openCardByUser} onEngagementStateChange={emitWorkflowContext} onApprovalSelected={(id) => openCardByUser('notifications', id)} openNotificationsPanel={openNotificationsPanel} onCloseNotificationsPanel={() => { dispatch(closeNotificationsPanel()); setMessages(prev => prev.filter(m => !(m.kind === 'cards' && (m as any).module === 'notifications'))); }} onOpenActionView={openActionView} onUserView={recordUserView} directEngagementId={directEngagementId} onDirectEngagementChange={setDirectEngagementId} />
    )
  );
  // PTT live transcript (what you're saying) — text/ptt modes only; never in a voice session.
  const userCaptionEl = <CaptionBox text={caption} show={chatVisible} tone="user" />;
  // SAM's reply above the cards, only on the mobile action/card surface (interactive only).
  // Prefer the turn-matched reply, but fall back to the latest assistant reply — in a voice turn the
  // turn-matched text bubble often isn't found (different id), which left this caption blank even
  // with CC on. latestAssistantText is the live reply for the just-shown cards (same as hands-free).
  const assistantCaptionEl = (
    <CaptionBox text={captionsOn ? (cardResponseText || latestAssistantText) : ''} show={showCardsSurface} tone="assistant" />
  );
  // Captions toggle + mic/speaker device picker, surfaced via the ⋮ menu across text, interactive
  // and hands-free. Captions live here now (the standalone CC pill was removed).
  const moreMenu = {
    captionsOn,
    onToggleCaptions: () => setCaptionsOn((p) => !p),
    inputs: audioInputs,
    outputs: audioOutputs,
    outputSupported,
    inputId: inputDeviceId,
    outputId: outputDeviceId,
    onSelectInput: setInputDeviceId,
    onSelectOutput: setOutputDeviceId,
    onOpen: refreshDevices,
  };
  // The composer's ⋮ additionally offers "Hands-free" (mobile only — hands-free is mobile-only and
  // is entered FROM text/PTT). Interactive/hands-free menus omit it (you're already in voice).
  const composerMoreMenu = {
    ...moreMenu,
    onEnterHandsFree: !isDesktop && modesAllowed.includes('interactive') ? enterHandsFree : undefined,
  };
  // Slim mic + speaker + ⋮ (+ End) row above the composer during an interactive session, plus an
  // optional text input (chat_settings.interactive_text_input_enabled) so the user can type to the
  // live realtime agent alongside voice.
  const voiceControls = isHandsFree ? (
    <>
      <VoicePills
        mode={mode}
        recording={recording}
        micMuted={micMuted}
        onToggleMic={toggleMicMute}
        speakerMuted={speakerMuted}
        onToggleSpeaker={toggleSpeakerMute}
        moreMenu={moreMenu}
        onEnd={stopInteractive}
      />
      {interactiveTextInputEnabled && <InteractiveTextInput onSend={sendRealtimeText} />}
    </>
  ) : null;
  // Push-to-talk recorder state + actions, handed to the composer (mic button + recorder bar).
  const ptt = {
    recording: isPtt && recording,
    locked: pttLocked,
    paused,
    playing: pttPlaying,
    seconds: Math.floor(recordMs / 1000),
    levels: pttLevels,
    start: startPtt,
    send: sendPtt,
    cancel: cancelPtt,
    pause: pausePtt,
    resume: resumePtt,
    lock: lockPtt,
    play: playPtt,
    stopPlayback: stopPttPlayback,
  };
  const composer = (
    <MessageComposer
      input={input}
      onInputChange={setInput}
      onKeyDown={handleKeyDown}
      onSend={submit}
      modesAllowed={modesAllowed}
      onToggleInteractive={toggleInteractive}
      onEnterHandsFree={!isDesktop && modesAllowed.includes('interactive') ? enterHandsFree : undefined}
      inVoice={isHandsFree}
      ptt={ptt}
      attachments={attach.attachments}
      onAddFiles={(files) => attach.addFiles(files, input)}
      onRemoveAttachment={attach.removeAttachment}
      uploading={attach.uploading}
      attachError={attach.error}
      fileAccept={fileAccept}
      uploadEnabled={chatConfig?.file_upload?.enabled !== false}
      moreMenu={composerMoreMenu}
    />
  );

  return (
    <div className="relative flex flex-col h-full bg-chat text-ui-text overflow-hidden">

      {/* View-switch loading overlay */}
      <div
        className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-chat/30 backdrop-blur-md transition-opacity duration-200"
        style={{ opacity: isSwitchingView ? 1 : 0, pointerEvents: isSwitchingView ? 'auto' : 'none' }}
      >
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-brand-bright animate-bounce"
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
        <p className="text-xs text-ui-text3 tracking-wide">
          {chatOn ? 'Getting things ready…' : 'Back to conversation…'}
        </p>
      </div>

      {/* Chat ↔ cards view toggle, rendered into the layout TopBar next to the profile menu.
          Label reflects the TARGET surface (what tapping switches TO): "UI" while on the thread,
          "Chat" while in the workspace — matching the tooltip. */}
      {topBarSlot && showViewToggle && createPortal(
        <button
          onClick={() => switchView(!chatOn)}
          title={chatOn ? 'Show cards' : 'Show chat'}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border border-ui-border bg-ui-subtle text-ui-text transition hover:bg-ui-hover"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${chatOn ? 'bg-success' : 'bg-brand-bright'}`} />
          {chatOn ? 'UI' : 'Chat'}
        </button>,
        topBarSlot,
      )}

      {headerVisible && (
        <ChatHeader
          showSessionsBtn={showSessionsBtn}
          showAgentPicker={showAgentPicker}
          agents={agents || []}
          assistantId={assistantId}
          onSelectAgent={selectAgent}
          onOpenSessions={() => setSessionsOpen(true)}
          showHandsFree={showHandsFree}
        />
      )}

      {!socketConnected && <OfflineBanner />}

      {showHandsFree ? (
        <HandsFreeView
          cards={draftIsCurrent || noteIsCurrent ? null : latestCards}
          draft={draftIsCurrent ? latestDraft : null}
          note={noteIsCurrent ? latestNote : null}
          index={hfIndex}
          setIndex={setHfIndex}
          banner={cardResponseText || latestAssistantText}
          recording={recording}
          captionsOn={captionsOn}
          onExit={exitHandsFree}
          micMuted={micMuted}
          onToggleMic={toggleMicMute}
          speakerMuted={speakerMuted}
          onToggleSpeaker={toggleSpeakerMute}
          moreMenu={moreMenu}
          history={recentViews.entries}
          onReplay={replayView}
          historyHasMore={recentViews.hasMore}
          onHistoryLoadMore={recentViews.loadMore}
          thread={thread}
          hasCards={!draftIsCurrent && !noteIsCurrent && !!latestCards}
          onDraftEdit={emitDraftContext}
          onDiscardDraft={discardDraftCard}
          onSentDraft={onDraftSent}
          onSendText={interactiveTextInputEnabled ? sendRealtimeText : undefined}
          onNoteClose={closeNoteCard}
          onNoteCaptureStart={onNoteCaptureStart}
          onNoteCaptureStop={onNoteCaptureStop}
          onNoteActive={onNoteActive}
          onOpenTask={(id) => { setOpenCards(prev => ({ ...prev, tasks: id })); openCardByUser('tasks', id); }}
          noteStopWord={noteStopWord}
        />
      ) : isDesktop ? (
        // Desktop — text AND interactive share this split layout (bubbles left, cards right). In
        // interactive, voiceControls add mic/speaker/End; no caption (the reply shows as a bubble).
        <div
          ref={splitContainerRef}
          className="flex-1 flex min-h-0"
          onMouseMove={(e) => {
            // e.buttons !== 1 catches the mouse re-entering the container after the primary
            // button was already released elsewhere (see the window-level mouseup listener above
            // for why splitDragging.current can otherwise be stuck true).
            if (!splitDragging.current || !splitContainerRef.current || e.buttons !== 1) {
              if (splitDragging.current && e.buttons !== 1) splitDragging.current = false;
              return;
            }
            const rect = splitContainerRef.current.getBoundingClientRect();
            const pct = Math.min(70, Math.max(25, ((rect.right - e.clientX) / rect.width) * 100));
            setWorkspacePct(pct);
          }}
        >
          <div className={`flex flex-col min-h-0 min-w-0 flex-1 ${latestSurface ? 'border-r border-ui-border' : ''}`}>
            <div className="flex-1 min-h-0">{thread}</div>
            {/* No SAM caption on desktop interactive — the reply shows as a bubble in the thread.
                Captions live only in hands-free and the mobile interactive UX/card surface. */}
            {isHandsFree ? null : userCaptionEl}
            {voiceControls}
            {composer}
          </div>
          {(isDesktop ? hasWorkspaceSurface : !!latestSurface) && (
            <>
              {/* Drag handle — grab to resize the split */}
              <div
                className="w-1 flex-shrink-0 cursor-col-resize hover:bg-brand-dark/20 active:bg-brand-dark/30 transition-colors group relative"
                onMouseDown={(e) => { e.preventDefault(); splitDragging.current = true; }}
                title="Drag to resize"
              >
                <div className="absolute inset-y-0 -left-1 -right-1" />
              </div>
              <div
                className="flex flex-col min-h-0 flex-shrink-0"
                style={{ width: `${workspacePct}%`, minWidth: 300, maxWidth: '70%' }}
              >
                {surfacePanel('', undefined, false)}
              </div>
            </>
          )}
        </div>
      ) : (
        // Mobile single-pane — text AND interactive. The thread is shown with NO caption — the reply
        // is already a bubble there (true for interactive too, so no live SAM caption over the chat);
        // an action flips chatOn=false → cards ("action mode") where the assistant caption shows
        // above the cards (thread hidden).
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0">
            {showWorkspace
              ? surfacePanel(isHandsFree ? '' : cardResponseText, tapToOpenChat ? () => openChat(latestSurface?.messageId) : undefined, !isHandsFree)
              : thread}
          </div>
          {showCardsSurface ? assistantCaptionEl : (isHandsFree ? null : userCaptionEl)}
          {voiceControls}
          {composer}
        </div>
      )}

      {multiThread && (
        <SessionsDrawer
          open={sessionsOpen}
          onClose={() => setSessionsOpen(false)}
          sessions={(allSessions || []).filter((s) => (s.agent_id || s.assistant_id || assistantId) === assistantId)}
          activeId={conversationId}
          onSelect={(id) => { setSessionsOpen(false); selectSession(id); }}
          onNew={() => { setSessionsOpen(false); startNewChat(); }}
          onDelete={removeSession}
        />
      )}

    </div>
  );
};

export default ChatPage;
